import React, { useEffect, useState } from 'react';
import { goToListing, type ListingAddress } from './listingAddress';
import type { ListingWords } from './listingWords';

// Sleek's price filter: a track with two handles over the listing's own price span, and two fields under
// it. Both edit one draft and nothing reaches the address until a handle is let go or a field is left, so
// dragging does not push a history step per pixel. An end left at the span's edge is not a filter at all.

/** What the price filter is drawn from: the listing's span, the address, the words, and the money unit. */
type PriceFilterProps = { span: { min: number; max: number }; address: ListingAddress; words: ListingWords; unit: string };

/** A price range being edited, as whole amounts. */
type Range = { min: number; max: number };

// Reads the range the address holds, filling an open end with the listing's own edge.
const rangeOf = (address: ListingAddress, low: number, high: number): Range =>
  ({ min: address.priceMin ?? low, max: address.priceMax ?? high });

// Puts an edited range in the address, from page one, when it differs from what is there.
function commitRange(address: ListingAddress, draft: Range, low: number, high: number): void {
  const min = Math.max(low, Math.min(draft.min, draft.max));
  const max = Math.min(high, Math.max(draft.min, draft.max));
  const priceMin = min > low ? min : null;
  const priceMax = max < high ? max : null;
  if (priceMin === address.priceMin && priceMax === address.priceMax) return;
  goToListing({ ...address, priceMin, priceMax, page: 1 });
}

// Says where a price sits along the track, as a share of it, for the filled stretch between the handles.
const share = (value: number, low: number, high: number): string => `${high === low ? 0 : ((value - low) / (high - low)) * 100}%`;

// Draws one number field of the range, committing when left or on Enter.
const PriceField: React.FC<{ label: string; value: number; unit: string; onChange: (value: number) => void; onCommit: () => void }> = ({ label, value, unit, onChange, onCommit }) => (
  <label className="xe-price-field">
    <span className="xe-sr-only">{label}</span>
    <input
      type="number" inputMode="numeric" min={0} value={Number.isFinite(value) ? value : ''}
      onChange={(event) => onChange(Number(event.target.value))} onBlur={onCommit}
      onKeyDown={(event) => { if (event.key === 'Enter') onCommit(); }}
    />
    <span className="xe-price-unit" aria-hidden="true">{unit}</span>
  </label>
);

// Draws the price filter, or nothing when every product in the listing costs the same.
export const ListingPriceFilter: React.FC<PriceFilterProps> = ({ span, address, words, unit }) => {
  const low = Math.floor(span.min);
  const high = Math.ceil(span.max);
  const [draft, setDraft] = useState<Range>(() => rangeOf(address, low, high));
  useEffect(() => setDraft(rangeOf(address, low, high)), [address, low, high]);
  if (high <= low) return null;
  const commit = () => commitRange(address, draft, low, high);
  const track = { '--xe-from': share(draft.min, low, high), '--xe-to': share(draft.max, low, high) } as React.CSSProperties;
  return (
    <div className="xe-price">
      <div className="xe-price-track" style={track}>
        <input type="range" min={low} max={high} value={draft.min} aria-label={words.from} onChange={(event) => setDraft({ ...draft, min: Math.min(Number(event.target.value), draft.max) })} onPointerUp={commit} onKeyUp={commit} />
        <input type="range" min={low} max={high} value={draft.max} aria-label={words.to} onChange={(event) => setDraft({ ...draft, max: Math.max(Number(event.target.value), draft.min) })} onPointerUp={commit} onKeyUp={commit} />
      </div>
      <div className="xe-price-fields">
        <PriceField label={words.from} value={draft.min} unit={unit} onChange={(min) => setDraft({ ...draft, min })} onCommit={commit} />
        <span className="xe-price-to">{words.to}</span>
        <PriceField label={words.to} value={draft.max} unit={unit} onChange={(max) => setDraft({ ...draft, max })} onCommit={commit} />
      </div>
    </div>
  );
};
